package link.erpango.quip.features.noteimport

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import link.erpango.quip.core.interactor.NoteImportBulkCreator
import link.erpango.quip.domain.entities.DVehicle
import link.erpango.quip.domain.noteimport.ImportDraft
import link.erpango.quip.domain.noteimport.ImportPlan
import link.erpango.quip.domain.noteimport.ItemStatus
import link.erpango.quip.domain.noteimport.MaintenanceDraft
import link.erpango.quip.domain.noteimport.MaintenanceField
import link.erpango.quip.domain.noteimport.NoteImportEvent
import link.erpango.quip.domain.noteimport.NoteImportTarget
import link.erpango.quip.domain.noteimport.SparePartDraft
import link.erpango.quip.domain.noteimport.SparePartField
import link.erpango.quip.domain.noteimport.VehicleDraft
import link.erpango.quip.domain.noteimport.VehicleField
import link.erpango.quip.domain.noteimport.VehicleRef
import link.erpango.quip.domain.noteimport.missingRequired
import link.erpango.quip.domain.usecases.INoteImportUseCase

enum class BulkStep { INPUT, REVIEW, CREATING, DONE }

/** What keeps a selected item from being created; the screen shows it as "Falta: …". */
enum class ReviewIssue {
    VIN, PLATE, BRAND, MODEL, YEAR, COLOR, OWNER,
    DATE, KILOMETERS, COST,
    CATEGORY, TYPE,
    /** A maintenance needs a vehicle. */
    VEHICLE,
    /** Its vehicle is a new one the user unchecked. */
    VEHICLE_NOT_SELECTED,
}

data class NoteImportBulkUiState(
    val step: BulkStep = BulkStep.INPUT,
    val analysis: NoteAnalysisState = NoteAnalysisState(),
    /** Working copy of the draft: the user's edits live here. */
    val draft: ImportDraft = ImportDraft(),
    val selected: Set<String> = emptySet(),
    val statuses: Map<String, ItemStatus> = emptyMap(),
    /** Garage vehicles, for "Asignar a…". */
    val garage: List<DVehicle> = emptyList(),
) {
    /** A vehicle already in the garage is not created: its items are attached to it. */
    fun isNew(vehicle: VehicleDraft): Boolean = vehicle.garageMatchVin == null

    /** Items that will actually be created (garage vehicles only attach). */
    val itemsToCreate: Int
        get() = draft.vehicles.count { it.id in selected && isNew(it) } +
            draft.maintenances.count { it.id in selected } +
            draft.spareParts.count { it.id in selected }

    fun issuesOf(vehicle: VehicleDraft): Set<ReviewIssue> =
        if (!isNew(vehicle)) emptySet() else vehicle.missingRequired().mapTo(mutableSetOf()) { it.toIssue() }

    fun issuesOf(maintenance: MaintenanceDraft): Set<ReviewIssue> = buildSet {
        maintenance.missingRequired().forEach { add(it.toIssue()) }
        if (!vehicleWillExist(maintenance.vehicle) && maintenance.vehicle != VehicleRef.Unassigned) add(ReviewIssue.VEHICLE_NOT_SELECTED)
    }

    fun issuesOf(part: SparePartDraft): Set<ReviewIssue> = buildSet {
        part.missingRequired().forEach { add(it.toIssue()) }
        if (!vehicleWillExist(part.vehicle) && part.vehicle != VehicleRef.Unassigned) add(ReviewIssue.VEHICLE_NOT_SELECTED)
    }

    /** Selected items that can not be created as they are. */
    val invalidSelected: Set<String>
        get() = buildSet {
            draft.vehicles.filter { it.id in selected && issuesOf(it).isNotEmpty() }.forEach { add(it.id) }
            draft.maintenances.filter { it.id in selected && issuesOf(it).isNotEmpty() }.forEach { add(it.id) }
            draft.spareParts.filter { it.id in selected && issuesOf(it).isNotEmpty() }.forEach { add(it.id) }
        }

    val canCreate: Boolean get() = step == BulkStep.REVIEW && itemsToCreate > 0 && invalidSelected.isEmpty()

    val failedIds: Set<String>
        get() = statuses.filterValues { it is ItemStatus.Failed || it is ItemStatus.Blocked }.keys

    /** Only selected items; a garage vehicle stays in the plan so its items can attach to it. */
    fun plan(): ImportPlan = ImportPlan(
        vehicles = draft.vehicles.filter { it.id in selected || !isNew(it) },
        maintenances = draft.maintenances.filter { it.id in selected },
        spareParts = draft.spareParts.filter { it.id in selected },
    )

    private fun vehicleWillExist(ref: VehicleRef): Boolean = when (ref) {
        is VehicleRef.Garage -> true
        is VehicleRef.Draft -> draft.vehicle(ref.draftId)?.let { !isNew(it) || it.id in selected } ?: false
        VehicleRef.Unassigned -> false
    }
}

private fun VehicleField.toIssue(): ReviewIssue = when (this) {
    VehicleField.VIN -> ReviewIssue.VIN
    VehicleField.PLATE -> ReviewIssue.PLATE
    VehicleField.BRAND -> ReviewIssue.BRAND
    VehicleField.MODEL -> ReviewIssue.MODEL
    VehicleField.YEAR -> ReviewIssue.YEAR
    VehicleField.COLOR -> ReviewIssue.COLOR
    VehicleField.OWNER -> ReviewIssue.OWNER
}

private fun MaintenanceField.toIssue(): ReviewIssue = when (this) {
    MaintenanceField.VEHICLE -> ReviewIssue.VEHICLE
    MaintenanceField.DATE -> ReviewIssue.DATE
    MaintenanceField.KILOMETERS -> ReviewIssue.KILOMETERS
    MaintenanceField.COST -> ReviewIssue.COST
}

private fun SparePartField.toIssue(): ReviewIssue = when (this) {
    SparePartField.CATEGORY -> ReviewIssue.CATEGORY
    SparePartField.TYPE -> ReviewIssue.TYPE
    SparePartField.BRAND -> ReviewIssue.BRAND
    SparePartField.MODEL -> ReviewIssue.MODEL
}

/**
 * "Importar desde nota" from the garage: paste a note, review every detected vehicle,
 * maintenance and spare part (select, edit, reassign) and create them all at once.
 *
 * Nothing is created until [create]; creation goes through [NoteImportBulkCreator] and a
 * retry only repeats what failed. The factory keeps this ViewModel for the session, so the
 * screen calls [start] when it opens and [reset] when it leaves.
 */
class NoteImportBulkViewModel(
    private val useCase: INoteImportUseCase,
    private val creator: NoteImportBulkCreator,
    private val loadGarage: suspend () -> List<DVehicle>,
) : ViewModel() {

    private val _state = MutableStateFlow(NoteImportBulkUiState())

    /** False until [start] and again after [reset]: the screen starts a fresh session only then. */
    var isStarted: Boolean = false
        private set

    val analysis = NoteAnalysisController(useCase, viewModelScope, onDraft = ::onDraft)

    val uiState: StateFlow<NoteImportBulkUiState> = combine(_state, analysis.state) { state, analysisState ->
        state.copy(analysis = analysisState)
    }.stateIn(viewModelScope, SharingStarted.Eagerly, NoteImportBulkUiState())

    /**
     * Opens the screen. With [note] and [draft] (coming from a form's dialog) it goes straight
     * to the review of that result.
     */
    fun start(note: String? = null, draft: ImportDraft? = null) {
        isStarted = true
        _state.value = NoteImportBulkUiState()
        analysis.start()
        useCase.track(NoteImportEvent.Opened(NoteImportTarget.BULK))
        viewModelScope.launch {
            val garage = runCatching { loadGarage() }.getOrDefault(emptyList())
            _state.update { it.copy(garage = garage) }
        }
        if (note != null) analysis.onNoteChange(note)
        if (draft != null) onDraft(draft)
    }

    fun backToInput() = _state.update { it.copy(step = BulkStep.INPUT) }

    fun toggle(id: String) = _state.update { state ->
        if (state.step != BulkStep.REVIEW) return@update state
        state.copy(selected = if (id in state.selected) state.selected - id else state.selected + id)
    }

    fun updateVehicle(id: String, transform: (VehicleDraft) -> VehicleDraft) = editDraft { draft ->
        draft.copy(vehicles = draft.vehicles.map { if (it.id == id) transform(it) else it })
    }

    fun updateMaintenance(id: String, transform: (MaintenanceDraft) -> MaintenanceDraft) = editDraft { draft ->
        draft.copy(maintenances = draft.maintenances.map { if (it.id == id) transform(it) else it })
    }

    fun updateSparePart(id: String, transform: (SparePartDraft) -> SparePartDraft) = editDraft { draft ->
        draft.copy(spareParts = draft.spareParts.map { if (it.id == id) transform(it) else it })
    }

    /** "Asignar a…": moves a maintenance or spare part to another vehicle. */
    fun assign(itemId: String, vehicle: VehicleRef) = editDraft { draft ->
        draft.copy(
            maintenances = draft.maintenances.map { if (it.id == itemId) it.copy(vehicle = vehicle) else it },
            spareParts = draft.spareParts.map { if (it.id == itemId) it.copy(vehicle = vehicle) else it },
        )
    }

    fun create() {
        val state = _state.value
        if (!state.canCreate && !(state.step == BulkStep.DONE && state.failedIds.isNotEmpty())) return
        val plan = state.plan()
        val previous = state.statuses
        _state.update { it.copy(step = BulkStep.CREATING) }
        viewModelScope.launch {
            val statuses = creator.execute(plan, previous) { id, status ->
                _state.update { it.copy(statuses = it.statuses + (id to status)) }
            }
            _state.update { it.copy(step = BulkStep.DONE, statuses = statuses) }
            val outcome = when {
                statuses.values.none { it is ItemStatus.Failed || it is ItemStatus.Blocked } -> "ok"
                statuses.values.any { it is ItemStatus.Created } -> "partial"
                else -> "failed"
            }
            useCase.track(NoteImportEvent.BulkCreated(outcome))
        }
    }

    /** Runs the same plan again: created items are skipped, so nothing is duplicated. */
    fun retryFailed() = create()

    /** Leaves the import. Ignored while creating: leaving the screen never interrupts it. */
    fun reset() {
        if (_state.value.step == BulkStep.CREATING) return
        isStarted = false
        analysis.clear()
        _state.value = NoteImportBulkUiState()
    }

    private fun editDraft(transform: (ImportDraft) -> ImportDraft) = _state.update { state ->
        if (state.step != BulkStep.REVIEW) state else state.copy(draft = transform(state.draft))
    }

    /** A new analysis replaces the working copy and preselects everything that can be created. */
    private fun onDraft(draft: ImportDraft) {
        _state.update {
            it.copy(
                step = BulkStep.REVIEW,
                draft = draft,
                statuses = emptyMap(),
                selected = buildSet {
                    draft.vehicles.filter { vehicle -> vehicle.garageMatchVin == null }.forEach { vehicle -> add(vehicle.id) }
                    draft.maintenances.forEach { maintenance -> add(maintenance.id) }
                    draft.spareParts.forEach { part -> add(part.id) }
                },
            )
        }
    }
}
